import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/services/api/apiRequest";
import { getConfig } from "@/services/api/config";
import { getClerkToken } from "@/services/clerk";
import type { AdminOrganizationRow, OrganizationDetail } from "../members.types";

/** One organization and whoever stewards it. */
export function useOrganization(id: string | undefined) {
  return useQuery<OrganizationDetail>({
    queryKey: ["afell", "organization", id],
    queryFn: () => apiRequest(`/afell/organizations/${id}/`),
    enabled: Boolean(id),
  });
}

/** The organizations the signed-in member stewards. */
export function useMyOrganizations() {
  return useQuery<{ organizations: OrganizationDetail[] }>({
    queryKey: ["afell", "organizations", "mine"],
    queryFn: () => apiRequest("/afell/organizations/"),
  });
}

/** Create an organization from its own form.
 *
 * An estate is an entity, not a label — so it comes into being filled in,
 * rather than as a name typed into a profile field. Naming one that already
 * exists joins it instead, and the response says which happened. */
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation<
    OrganizationDetail & { joined: boolean },
    Error,
    // `relationship` is about the creator's link to the entity, not about the
    // entity — it rides along on creation so nobody has to say it twice.
    Partial<OrganizationDetail> & {
      name: string;
      relationship?: string[];
      relationship_other?: string;
    }
  >({
    mutationFn: (payload) => apiRequest("/afell/organizations/", "POST", payload),
    onSuccess: () => {
      // A new organization is a directory entry and a change to the member's
      // own affiliation, which the profile and the sidebar both read.
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
    },
  });
}

export function useUpdateOrganization(id: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation<OrganizationDetail, Error, Partial<OrganizationDetail>>({
    mutationFn: (payload) =>
      apiRequest(`/afell/organizations/${id}/`, "POST", payload),
    onSuccess: () => {
      // The edit shows on the directory and on the org page, so both go stale.
      queryClient.invalidateQueries({ queryKey: ["afell", "organization"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
    },
  });
}

/** Existing organizations, for the "add an organization" pickers.
 *
 * Two stewards of one estate should land on one row. Name matching is
 * case-insensitive server-side, but "Estate of Jack Tworkov" and "Jack Tworkov
 * Estate" are two rows and a forked directory — so the editor offers what
 * already exists rather than trusting everyone to type it the same way. */
export function useOrganizationSearch(query: string) {
  return useQuery<{
    organizations: {
      id: number;
      name: string;
      stewards: number;
      yours: boolean;
    }[];
  }>({
    queryKey: ["afell", "organizations", "search", query],
    queryFn: () =>
      apiRequest(`/afell/organizations/search/?q=${encodeURIComponent(query)}`),
    placeholderData: keepPreviousData,
  });
}

/** New directory entries waiting for an admin. */
export function usePendingOrganizations() {
  return useQuery<{
    organizations: (OrganizationDetail & {
      createdBy: string;
      createdByEmail: string;
      createdByListed: boolean;
      dateCreated: string;
      /** Existing legacies this might be under another wording. Flagged, never
       *  refused: an estate and a studio may be one body or two, and a rule
       *  cannot tell — so the reviewer is handed the candidates. */
      possibleDuplicates: {
        id: number;
        name: string;
        status: string;
        score: number;
        why: string;
      }[];
    })[];
  }>({
    queryKey: ["afell", "admin", "organizations"],
    queryFn: () => apiRequest("/afell/admin/organizations/"),
  });
}

/** Approve or decline one. */
export function useReviewOrganization() {
  const queryClient = useQueryClient();
  return useMutation<
    OrganizationDetail,
    Error,
    { id: number; status: "approved" | "declined" | "pending"; note?: string }
  >({
    mutationFn: ({ id, status, note }) =>
      apiRequest(`/afell/admin/organizations/${id}/review/`, "POST", {
        status,
        note,
      }),
    onSuccess: () => {
      // Approving publishes a directory entry and gives its stewards an
      // affiliation, so the directory and the member payloads both go stale.
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
    },
  });
}


/** Every entity, for the admin table. */
export type AdminOrganizationsPage = {
  organizations: AdminOrganizationRow[];
  total: number;
  page: number;
  pageSize: number;
};

/** Paged and sorted server-side — 430 rows in one response is what made this
 *  tab slow to open long after the query count came down. */
export function useAllOrganizations(
  page = 1,
  sort: string | null = null,
  direction: "asc" | "desc" = "asc"
) {
  return useQuery<AdminOrganizationsPage>({
    queryKey: ["afell", "admin", "organizations", "all", page, sort, direction],
    queryFn: () =>
      apiRequest(
        `/afell/admin/organizations/all/?page=${page}` +
          (sort ? `&sort=${sort}&direction=${direction}` : "")
      ),
    placeholderData: keepPreviousData,
  });
}

/** An admin unlinking a steward who has left.
 *
 * Unlisting the entity is not this: that is the entity's visibility, while the
 * affiliation under a member's name comes from the link (Sami, 2026-09-22,
 * about Simon Castets and LUMA Arles). The member's own version is leaving. */
export function useRemoveSteward(organizationId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation<{ removed: string; lost_coverage: boolean }, Error, { memberId: number }>({
    mutationFn: ({ memberId }) =>
      apiRequest(
        `/afell/organizations/${organizationId}/stewards/${memberId}/remove/`,
        "POST"
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "organization", String(organizationId)] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] });
    },
  });
}

/** In or out of the directory — visibility, not review. */
export function useSetOrganizationListed() {
  const queryClient = useQueryClient();
  return useMutation<{ id: number; listed: boolean }, Error, { id: number; listed: boolean }>({
    mutationFn: ({ id, listed }) =>
      apiRequest(`/afell/admin/organizations/${id}/listing/`, "POST", { listed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
    },
  });
}


/** Multipart upload of a legacy's picture — bypasses apiRequest's JSON encoding. */
export function useSetOrganizationImage(id: number) {
  const queryClient = useQueryClient();
  return useMutation<{ image_url: string }, Error, File>({
    mutationFn: async (file) => {
      const body = new FormData();
      body.append("image", file);
      const headers: Record<string, string> = {};
      const token = await getClerkToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(`${getConfig().apiUrl}/afell/organizations/${id}/image/`, {
        method: "POST",
        headers,
        body,
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Upload failed");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
    },
  });
}

export function useRemoveOrganizationImage(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest(`/afell/organizations/${id}/image/`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
    },
  });
}

/** Put a declined entry back in the queue, corrected. */
export function useResubmitOrganization() {
  const queryClient = useQueryClient();
  return useMutation<{ id: number; status: string }, Error, number>({
    mutationFn: (id) => apiRequest(`/afell/organizations/${id}/resubmit/`, "POST"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
    },
  });
}

/** Delete an entry nobody else stewards. Sharing one means unlinking instead. */
export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, number>({
    mutationFn: (id) => apiRequest(`/afell/organizations/${id}/delete/`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
    },
  });
}

/** What you are to one legacy — a property of the link, so it is written one
 *  link at a time rather than inside a payload that rewrites them all. */
export function useSetRelationship(id: number) {
  const queryClient = useQueryClient();
  return useMutation<
    { id: number; relationship: string[] },
    Error,
    { relationship: string[]; other?: string }
  >({
    mutationFn: ({ relationship, other }) =>
      apiRequest(`/afell/organizations/${id}/relationship/`, "POST", {
        relationship,
        relationship_other: other ?? "",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "organization"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
    },
  });
}

/** Stop stewarding one. The row stays — it is shared; deleting is the last
 *  steward's. `confirm` answers the warning when a legacy covers your
 *  membership and leaving would end it. */
export function useLeaveOrganization() {
  const queryClient = useQueryClient();
  return useMutation<
    { id: number; left: string },
    Error,
    { id: number; confirm?: boolean }
  >({
    mutationFn: ({ id, confirm }) =>
      apiRequest(`/afell/organizations/${id}/leave/`, "POST", { confirm }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "organization"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
    },
  });
}

/** Organizations with a paying member, for the fee-waiver selector.
 *
 * `yours` marks the ones the member actually stewards, which are the only ones
 * a claim can be made through. Signed-in only: which organizations hold paid
 * memberships is not something to publish. */
export function useFundedOrganizations(enabled = true) {
  return useQuery<{ organizations: { id: number; name: string; yours: boolean }[] }>({
    queryKey: ["afell", "organizations", "funded"],
    queryFn: () => apiRequest("/afell/organizations/funded/"),
    enabled,
  });
}

/** Ask for membership through an organization already on your profile. Naming
 *  the organization is not the claim; this is, and an administrator answers it
 *  with the application it arrived on. */
export function useRequestOrgSponsorship() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { organization_id: number; note?: string }>({
    mutationFn: (body) => apiRequest("/afell/me/org-claims/request/", "POST", body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["afell", "org-claims"] }),
  });
}
